"use client";

import type { TUser } from "@/app/(auth)/_utils/functions/getUser.ts";
import type { IExtraUserData } from "@/app/[project-id]/_utils/types/types";
import { COGNITO_PRESENTATION_MODE_ATTRIBUTE } from "@/app/_utils/constants/presentationMode.ts";
import type { IOrganizationDocument } from "@/app/_utils/database/db-models/organization.ts";
import * as Sentry from "@sentry/nextjs";
import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

export interface IUserContext {
  "user": Partial<TUser> | null;
  "setUser": Dispatch<SetStateAction<Partial<TUser> | null>>;
}

export const UserContext = createContext<IUserContext>({
  "user": {
    "activeOrganization": "",
    "organizations": [],
    "sharedOrganizations": [],
    "sub": "",
    "name": "",
    "email": "",
    "picture": "",
    "custom:email_aliases": "[]",
    "extra": {
      "atomicNumbers": {},
    },
  },
  "setUser": () => undefined,
});

/**
 * Merges layout props with the Cognito-backed user payload.
 *
 * @param activeUser - User from the ID token (or null when logged out).
 * @param activeOrganization - Active organization id from the server.
 * @param organizations - Organizations the user belongs to.
 * @param sharedOrganizations - Organizations shared with the user.
 * @param picture - Resolved profile image URL.
 * @param extra - Extra client data such as the atomic numbers map.
 *
 * @returns Merged user object for context, or null when logged out.
 */
function buildMergedUser(
  activeUser: TUser | null,
  activeOrganization: string,
  organizations: Partial<IOrganizationDocument>[],
  sharedOrganizations: Partial<IOrganizationDocument>[],
  picture: string,
  extra: IExtraUserData,
): Partial<TUser> | null {
  if (activeUser === null) {
    return null;
  }

  // Spread token claims first so server-fetched org lists and profile fields always win. Otherwise
  // a JWT payload that includes empty `organizations` / `sharedOrganizations` (or stale values)
  // overwrites DB-backed lists and can break `userHasPresentationOrganizationAccess` after refresh.
  return {
    ...activeUser,
    "activeOrganization": activeOrganization,
    "organizations": organizations,
    "sharedOrganizations": sharedOrganizations,
    "picture": picture,
    "extra": extra,
  };
}

/**
 * Stable fingerprint of server-driven user fields so client state can reset when the server
 * snapshot changes without clobbering purely client-side updates.
 *
 * @param activeUser - User from the ID token (or null when logged out).
 * @param activeOrganization - Active organization id from the server.
 * @param organizations - Organizations the user belongs to.
 * @param sharedOrganizations - Organizations shared with the user.
 * @param picture - Resolved profile image URL.
 * @param extra - Extra client data such as the atomic numbers map.
 *
 * @returns Single string key for comparison.
 */
function serverUserSyncKey(
  activeUser: TUser | null,
  activeOrganization: string,
  organizations: Partial<IOrganizationDocument>[],
  sharedOrganizations: Partial<IOrganizationDocument>[],
  picture: string,
  extra: IExtraUserData,
): string {
  if (activeUser === null) {
    return "null";
  }

  const orgIds = organizations
    .map((o) => o._id)
    .filter((id): id is string => typeof id === "string")
    .join(",");
  const sharedIds = sharedOrganizations
    .map((o) => o._id)
    .filter((id): id is string => typeof id === "string")
    .join(",");

  return [
    activeUser.sub,
    activeUser[COGNITO_PRESENTATION_MODE_ATTRIBUTE] ?? "",
    activeOrganization,
    picture,
    orgIds,
    sharedIds,
    activeUser.email ?? "",
    activeUser.name ?? "",
    JSON.stringify(extra),
  ].join("|");
}

/**
 * Provides user data to child components.
 *
 * @param params - Parameters passed from the parent object.
 *
 * @returns User context provider element.
 */
export default function UserDataProvider({
  children,
  activeUser,
  organizations,
  sharedOrganizations,
  activeOrganization,
  picture,
  extra,
}: {
  children: React.ReactNode;
  activeUser: TUser | null;
  organizations: Partial<IOrganizationDocument>[];
  sharedOrganizations: Partial<IOrganizationDocument>[];
  activeOrganization: string;
  picture: string;
  extra: IExtraUserData;
}): React.ReactElement {
  const mergedFromServer = useMemo(
    () =>
      buildMergedUser(
        activeUser,
        activeOrganization,
        organizations,
        sharedOrganizations,
        picture,
        extra,
      ),
    [
      activeUser,
      activeOrganization,
      organizations,
      sharedOrganizations,
      picture,
      extra,
    ],
  );

  const serverSyncKey = useMemo(
    () =>
      serverUserSyncKey(
        activeUser,
        activeOrganization,
        organizations,
        sharedOrganizations,
        picture,
        extra,
      ),
    [
      activeUser,
      activeOrganization,
      organizations,
      sharedOrganizations,
      picture,
      extra,
    ],
  );

  const [user, setUser] = useState<Partial<TUser> | null>(() =>
    buildMergedUser(
      activeUser,
      activeOrganization,
      organizations,
      sharedOrganizations,
      picture,
      extra,
    ),
  );

  const [lastServerSyncKey, setLastServerSyncKey] = useState(serverSyncKey);

  if (serverSyncKey !== lastServerSyncKey) {
    setLastServerSyncKey(serverSyncKey);
    setUser(mergedFromServer);
  }

  useEffect(() => {
    if (user?.email === undefined || user.name === undefined) {
      Sentry.setUser(null);

      return;
    }

    Sentry.setUser({
      email: user.email,
      username: user.name,
      id: user.sub ?? "",
    });
  }, [user]);

  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
}
